import React, { useContext } from 'react';
import times from 'lodash.times';

import useMedia from 'hooks/useMedia';
import getMargins from 'components/data-visualization/LineChart/helpers/getMargins';

import { EntityContext } from 'context/entityContext';
import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import ComponentControls from 'components/styled/ComponentControls/ComponentControls';
import KeyValueLoader from 'components/utility/ContentLoader/KeyValueLoader/KeyValueLoader';
import {
  FakeButtonWrapper,
  FakeButton,
  FakeDropdown,
} from 'components/base/SkeletonLoader/FakeInteractive';
import {
  VisiblePeriodWrapper,
  StyledPerformanceTrendLoader,
  Relative,
} from './shared/PerformanceTrend.style';

import LineChartSkeleton from 'components/data-visualization/LineChart/LineChartSkeleton/LineChartSkeleton';
import Chart from 'components/data-visualization/Chart/Chart';
import { ILabelValuePair } from 'types/shared';

type IExtendeddLabelValuePair = ILabelValuePair<string> & {
  active: boolean;
};

interface ISkeletonLoaderProps {
  title: string;
  dropdownLabel?: string;
  toggleOptions?: IExtendeddLabelValuePair[];
  showComplementary?: boolean;
  showPeriodDropdown?: boolean;
  showSplitLineCharts?: boolean;
  splitChartCount?: any;
  isWide?: boolean;
  isNarrowScreen?: boolean;
}

const SkeletonLoader: React.FC<ISkeletonLoaderProps> = (props) => {
  const context = useContext(EntityContext);
  const { compareMode } = context;

  const {
    title,
    dropdownLabel,
    toggleOptions,
    showComplementary,
    showPeriodDropdown,
    showSplitLineCharts,
    splitChartCount,
    isWide,
    isNarrowScreen,
  } = props;

  const isSmallScreen = useMedia('(max-width: 48em)');
  const margin = getMargins(true);

  return (
    <GenericComponent
      title={title}
      loading={true}
      isWide={isWide}
      isNarrowScreen={isNarrowScreen}
    >
      <AnimatedLoadingWrapper loading={true}>
        <ComponentControls>
          <VisiblePeriodWrapper>
            <KeyValueLoader />
          </VisiblePeriodWrapper>

          {!compareMode ? (
            <FakeButtonWrapper>
              {toggleOptions &&
                toggleOptions.map((option: IExtendeddLabelValuePair) => (
                  <FakeButton key={option.value} active={option.active}>
                    {option.label}
                  </FakeButton>
                ))}
            </FakeButtonWrapper>
          ) : (
            <FakeDropdown>{dropdownLabel}</FakeDropdown>
          )}
          {showPeriodDropdown && <FakeDropdown>View By Week</FakeDropdown>}
        </ComponentControls>

        {showSplitLineCharts ? (
          times(splitChartCount, (idx) => (
            <Relative key={`split-chart-${idx}`}>
              <Chart
                height={260}
                mediaQuery="(min-width: 40em)"
                isDimmed
                isComplementary
                render={(renderProps): JSX.Element => (
                  <LineChartSkeleton
                    width={renderProps.parentWidth}
                    height={
                      renderProps.parentHeight - margin.top - margin.bottom
                    }
                    marginTop={margin.top}
                  />
                )}
              />
              <StyledPerformanceTrendLoader />
            </Relative>
          ))
        ) : (
          <>
            <Relative>
              <Chart
                height={isSmallScreen ? 260 : 420}
                mediaQuery="(min-width: 40em)"
                isDimmed
                hasComplementary={showComplementary}
                render={(renderProps): JSX.Element => (
                  <LineChartSkeleton
                    width={renderProps.parentWidth}
                    height={
                      renderProps.parentHeight - margin.top - margin.bottom
                    }
                    marginTop={margin.top}
                  />
                )}
              />
              <StyledPerformanceTrendLoader />
            </Relative>

            {showComplementary && (
              <Relative>
                <Chart
                  height={260}
                  mediaQuery="(min-width: 40em)"
                  isDimmed
                  isComplementary
                  render={(renderProps): JSX.Element => (
                    <LineChartSkeleton
                      width={renderProps.parentWidth}
                      height={
                        renderProps.parentHeight - margin.top - margin.bottom
                      }
                      marginTop={margin.top}
                    />
                  )}
                />
                <StyledPerformanceTrendLoader />
              </Relative>
            )}
          </>
        )}
      </AnimatedLoadingWrapper>
    </GenericComponent>
  );
};

export default SkeletonLoader;
